import type { FilterType } from "enums/components";
import type { RecordFormField, RecordFormTab } from "features/shared/components/dynamic-form/types";
import type { TFunction } from "i18next";

import { FilterTypes } from "enums/components";

import type { DrawerOptions } from "./fieldKindStrategies";

import { getFieldKindStrategy } from "./fieldKindStrategies";

export const RECORD_FORM_FIELD_HEIGHT = "4.8rem";

export const getRecordFormFieldLayoutProps = (filterType: FilterType) =>
  filterType === FilterTypes.SWITCH
    ? { className: "w-full max-w-full", width: "100%" }
    : { className: "min-w-0 w-full", height: RECORD_FORM_FIELD_HEIGHT, width: "100%" };

const getRecordFormFieldTypeProps = ({
  drawerOptions,
  filterType,
  inputType,
}: {
  drawerOptions?: DrawerOptions;
  filterType: FilterType;
  inputType?: "number" | "string";
}) => {
  if (filterType === FilterTypes.SWITCH) return {};
  if (filterType === FilterTypes.COLOR_INPUT) return { width: "15rem" };
  return { type: inputType, ...drawerOptions };
};

type BuildRecordFormFieldOptions<TKey extends string> = {
  colSpan?: 1 | 2;
  config: Record<string, unknown>;
  drawerOptions?: DrawerOptions;
  filterType: FilterType;
  inputType?: "number" | "string";
  key: TKey;
  label: string;
  onFieldChange: (field: TKey, value: unknown) => void;
  section?: string;
  tab?: string;
};

export const buildRecordFormField = <TKey extends string>({
  colSpan,
  config,
  drawerOptions,
  filterType,
  inputType,
  key,
  label,
  onFieldChange,
  section,
  tab,
}: BuildRecordFormFieldOptions<TKey>): RecordFormField => ({
  colSpan,
  config,
  filterName: key,
  filterType,
  props: {
    ...getRecordFormFieldLayoutProps(filterType),
    label,
    ...getRecordFormFieldTypeProps({ drawerOptions, filterType, inputType }),
  },
  section,
  setConfig: (_, newValue) => onFieldChange(key, newValue),
  tab,
});

export type RecordFormFieldDefinition = {
  colSpan?: 1 | 2;
  filterType: FilterType;
  inputType?: "number" | "string";
  key: string;
  kind: Parameters<typeof getFieldKindStrategy>[0];
  options?: readonly (number | string)[];
  section?: string;
  tab?: string;
};

type BuildRecordFormFieldsOptions<
  TFormState extends Record<string, unknown>,
  TFieldKey extends keyof TFormState & string = keyof TFormState & string
> = {
  definitions: readonly (RecordFormFieldDefinition & { key: TFieldKey })[];
  formState: TFormState;
  getDrawerOptions?: (field: RecordFormFieldDefinition & { key: TFieldKey }) => DrawerOptions | undefined;
  getLabel: (field: RecordFormFieldDefinition & { key: TFieldKey }) => string;
  onFieldChange: (field: TFieldKey, value: unknown) => void;
};

export const buildRecordFormFields = <
  TFormState extends Record<string, unknown>,
  TFieldKey extends keyof TFormState & string = keyof TFormState & string
>({
  definitions,
  formState,
  getDrawerOptions,
  getLabel,
  onFieldChange,
}: BuildRecordFormFieldsOptions<TFormState, TFieldKey>): RecordFormField[] =>
  definitions.map((field) =>
    buildRecordFormField({
      config: formState,
      drawerOptions: getDrawerOptions?.(field) ?? getFieldKindStrategy(field.kind).getDrawerOptions?.(field),
      label: getLabel(field),
      onFieldChange,
      ...field,
    })
  );

export const buildRecordFormTabs = (tabNames: readonly string[], t: TFunction, labelPrefix: string): RecordFormTab[] =>
  tabNames.map((name) => ({
    label: String(t(`${labelPrefix}.${name}`)),
    name,
  }));
